import toString from 'lodash/toString'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { produce } from 'immer'
import constant from 'lodash/constant'
import isError from 'lodash/isError'
import { Check, Plus } from 'lucide-react'
import { Card } from '@klear/ui/components/card'
import { toast } from '@klear/ui/hooks/use-toast'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { bindWizardSteps, type WizardStepModule } from '@klear/ui/wizard/flow'
import { WizardFlowContent } from '@klear/ui/wizard/flow-content'
import { useReportedWizardFlow } from '@klear/ui/wizard/use-step-group-nav'
import { ROUTE_SUPPLIERS, buildNavPathTrader } from '@klear/constants'
import {
  clearOperationalFormErrors,
  hasOperationalFormErrors,
  operationalFormFromStored,
  operationalFormToCreateWire,
  operationalFormToEventData,
  operationalFormToUpdateWire,
  validateOperationalDetails,
  type OperationalEventData,
  type OperationalForm,
  type OperationalFormErrorMap
} from '@klear/operational/wire.client'
import { OperationalDetailsFields, OperationalTermsFields } from '@/app/operational/workflow/operational-fields'
import { useOperationalMutations } from '@/app/operational/workflow/use-operational'
import { CreateEntityReturnLink } from '@/app/workflow/create-entity-return-link'

export type OperationalStepGroupResult = {
  operationalId: number
  eventData: OperationalEventData
}

export type OperationalFlowMode = 'create' | 'edit'

const STEP = {
  DETAILS: 'operational-details',
  TERMS: 'operational-terms'
} as const

type StepId = (typeof STEP)[keyof typeof STEP]

type OperationalStepCtx = {
  traderId: number
  vals: OperationalForm
  errors: OperationalFormErrorMap
  handleFieldChange: <K extends keyof OperationalForm>(field: K, value: OperationalForm[K]) => void
  validateBeforeNext: (stepId: StepId) => boolean
}

const detailsStep: WizardStepModule<OperationalStepCtx> = {
  id: STEP.DETAILS,
  label: 'Details',
  isComplete: (ctx) => {
    const title = ctx.vals.title.trim()
    const supplierId = ctx.vals.supplierId.trim()
    return title.length > 0 && supplierId.length > 0
  },
  beforeNext: (ctx) => ctx.validateBeforeNext(STEP.DETAILS),
  component: function OperationalDetailsStepContent({ ctx }) {
    return (
      <Card className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Operational details</h2>
        <p className="text-sm text-muted-foreground">Who is supplying, and how this operational event is identified.</p>
        <OperationalDetailsFields
          traderId={ctx.traderId}
          values={ctx.vals}
          errors={ctx.errors}
          onFieldChange={ctx.handleFieldChange}
          createSupplierAction={
            <CreateEntityReturnLink to={buildNavPathTrader(toString(ctx.traderId), ROUTE_SUPPLIERS, 'new')}>
              <Plus className="h-3 w-3 mr-1" />
              Create supplier
            </CreateEntityReturnLink>
          }
        />
      </Card>
    )
  }
}

const termsStep: WizardStepModule<OperationalStepCtx> = {
  id: STEP.TERMS,
  label: 'Terms',
  isComplete: constant(true),
  component: function OperationalTermsStepContent({ ctx }) {
    return (
      <Card className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Operational terms</h2>
        <p className="text-sm text-muted-foreground">Value, dates, and any notes for this operational event.</p>
        <OperationalTermsFields
          traderId={ctx.traderId}
          values={ctx.vals}
          errors={ctx.errors}
          onFieldChange={ctx.handleFieldChange}
        />
      </Card>
    )
  }
}

const OPERATIONAL_STEP_MODULES = [detailsStep, termsStep]

export type OperationalFlowOptions = {
  traderId: number
  orderId: number
  parentEventId?: number | null
  supplierIdSeed?: number | null
  initialData?: OperationalEventData | null
  existingId?: number | null
}

export type OperationalFlowProps = OperationalFlowOptions & {
  mode: OperationalFlowMode
  cancelOnFirstStep?: boolean
  onNavStateChange: (nav: WizardStepGroupNavState) => void
  onComplete: (result: OperationalStepGroupResult) => void
  onCancel: () => void
  onDirtyChange?: (dirty: boolean) => void
}

export function OperationalFlow({
  mode,
  traderId,
  orderId,
  parentEventId,
  supplierIdSeed = null,
  initialData = null,
  existingId = null,
  cancelOnFirstStep = false,
  onNavStateChange,
  onComplete,
  onCancel,
  onDirtyChange
}: OperationalFlowProps) {
  const { commitOperational, isCommitting } = useOperationalMutations()
  const [values, setValues] = useState<OperationalForm>(() => {
    const stored = operationalFormFromStored(initialData)
    if (supplierIdSeed == null || supplierIdSeed < 1) return stored
    return { ...stored, supplierId: toString(supplierIdSeed) }
  })
  const [errors, setErrors] = useState<OperationalFormErrorMap>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  const handleFieldChange = useCallback(<K extends keyof OperationalForm>(field: K, value: OperationalForm[K]) => {
    setDirty(true)
    setValues((current) =>
      produce(current, (draft) => {
        draft[field] = value as never
      })
    )
    setErrors((current) => clearOperationalFormErrors(current, [field]))
  }, [])

  const validateBeforeNext = useCallback(
    (stepId: StepId) => {
      if (stepId !== STEP.DETAILS) return true
      const validationErrors = validateOperationalDetails(values)
      if (hasOperationalFormErrors(validationErrors)) {
        setErrors(validationErrors)
        return false
      }
      return true
    },
    [values]
  )

  const handleSubmit = useCallback(async () => {
    if (orderId < 1) return
    setSaveError(null)

    const linkParentId = parentEventId != null && parentEventId > 0 ? parentEventId : null
    const editId = existingId != null && existingId > 0 ? existingId : null

    try {
      let result
      if (mode === 'edit' && editId != null) {
        result = await commitOperational({ id: editId, traderId, data: operationalFormToUpdateWire(values) })
      } else {
        const data = operationalFormToCreateWire(values, orderId)
        if (linkParentId != null) data.parentEventId = linkParentId
        result = await commitOperational({ traderId, data })
      }

      if (result?.id == null) {
        throw new Error('Saving the operational event did not return an id.')
      }
      setDirty(false)
      onComplete({ operationalId: result.id, eventData: operationalFormToEventData(values) })
    } catch (err: unknown) {
      const message = isError(err) ? err.message : 'Failed to save the operational event.'
      setSaveError(message)
      toast({ title: 'Save failed', description: message, variant: 'destructive' })
    }
  }, [commitOperational, existingId, mode, onComplete, orderId, parentEventId, traderId, values])

  const stepCtx = useMemo<OperationalStepCtx>(
    () => ({
      traderId,
      vals: values,
      errors,
      handleFieldChange,
      validateBeforeNext
    }),
    [traderId, values, errors, handleFieldChange, validateBeforeNext]
  )

  const stepDefs = useMemo(() => bindWizardSteps(OPERATIONAL_STEP_MODULES, stepCtx), [stepCtx])

  const flow = useReportedWizardFlow(stepDefs, {
    stepSelection: 'gated',
    onNavStateChange,
    onExitFirstStep: cancelOnFirstStep ? onCancel : undefined
  })

  return (
    <WizardFlowContent
      flow={flow}
      onSubmit={() => void handleSubmit()}
      cancelOnFirstStep={cancelOnFirstStep}
      disabled={isCommitting}
      submitIcon={<Check className="h-4 w-4 mr-1" />}
      submitLabel={isCommitting ? 'Saving…' : mode === 'edit' ? 'Save & continue' : 'Create operational'}
      navClassName="flex justify-between gap-2"
    >
      {saveError ? <p className="text-sm text-destructive">{saveError}</p> : null}
    </WizardFlowContent>
  )
}
